// Utility to generate realistic test data for notifications
import { supabase } from '@/lib/supabaseClient';

export const testDataGenerator = {
  // Generate a realistic date (within the next 30 days)
  generateRealisticDate(): string {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + Math.floor(Math.random() * 30) + 1);
    return futureDate.toISOString().split('T')[0];
  },

  // Generate a realistic time
  generateRealisticTime(): string {
    const hours = Math.floor(Math.random() * 12) + 8; // 8 AM to 8 PM
    const minutes = Math.random() > 0.5 ? '00' : '30';
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes} ${ampm}`;
  },

  // Generate a realistic meeting link
  generateMeetingLink(): string {
    const platforms = [
      'https://meet.google.com/',
      'https://meet.google.com/',
      'https://meet.google.com/',
      'https://meet.google.com/'
    ];
    const platform = platforms[Math.floor(Math.random() * platforms.length)];
    const meetingId = Math.random().toString(36).substring(2, 15);
    return `${platform}${meetingId}`;
  },

  // Generate a realistic project URL
  generateProjectUrl(projectId?: string): string {
    const id = projectId || '93f99a29-2854-40f8-a128-f8e56bf92a0c'; // Use real project UUID
    return `https://app.joulescrm.com/app/pipeline-resi?projectId=${id}&modal=true`;
  },

  // Generate a realistic ticket URL
  generateTicketUrl(ticketId?: string): string {
    const id = ticketId || `TICK-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
    return `https://app.joulescrm.com/app/support-tickets/${id}`;
  },

  // Generate a realistic booking URL
  generateBookingUrl(): string {
    const bookingId = `booking-${Math.random().toString(36).substring(2, 8)}`;
    return `https://app.joulescrm.com/app/scheduler/appointments?showAllBookings=true&bookingId=${bookingId}`;
  },

  // Fetch real projects from current tenant for testing (with cache busting)
  async getRealProjectsFromCurrentTenant(): Promise<Array<{project_id: string, project_name: string, project_type: string, pipeline_id: number}>> {
    try {
      // Add timestamp to prevent caching and get fresh data
      const timestamp = Date.now();
      
      const { data, error } = await supabase
        .from('projects')
        .select('id, project_name, project_type, pipeline_id, updated_at')
        .order('updated_at', { ascending: false }) // Get most recently updated first
        .limit(10); // Get more projects for variety
        
      if (error) {
        console.error('Error fetching real projects:', error);
        // Fallback to default data if database fetch fails
        return [{
          project_id: 'fallback-project-id',
          project_name: 'Test Project',
          project_type: 'Residential',
          pipeline_id: 1
        }];
      }
      
      
      return (data || []).map(project => ({
        project_id: project.id,
        project_name: project.project_name,
        project_type: project.project_type,
        pipeline_id: project.pipeline_id
      }));
    } catch (error) {
      console.error('Error in getRealProjectsFromCurrentTenant:', error);
      // Fallback to default data if database fetch fails
      return [{
        project_id: 'fallback-project-id',
        project_name: 'Test Project', 
        project_type: 'Residential',
        pipeline_id: 1
      }];
    }
  },

  // Generate realistic sample data based on notification type
  async generateSampleDataForType(notificationType: string): Promise<Record<string, any>> {
    const baseData: Record<string, any> = {};
    
    switch (notificationType) {
      case 'booking_reminder_1hr_host':
      case 'booking_reminder_1hr_attendee':
      case 'booking_reminder_5min_host':
      case 'booking_reminder_5min_attendee':
      case 'booking_scheduled_host':
      case 'booking_scheduled_attendee':
        baseData.booking_date = this.generateRealisticDate();
        baseData.booking_time = this.generateRealisticTime();
        baseData.customer_name = 'Sarah Johnson';
        baseData.customer_email = 'sarah.johnson@example.com';
        baseData.host_name = 'Mike Chen'; // Added missing host_name field
        baseData.meeting_link = this.generateMeetingLink();
        baseData.calendar_url = this.generateBookingUrl();
        baseData.booking_url = `https://app.joulescrm.com/book/site-survey-${Math.random().toString(36).substring(2, 6)}`;
        baseData.project_name = 'Johnson Residence Solar Installation';
        // Add custom questions fields that templates might expect
        baseData.custom_questions_html = '<p><strong>Installation preferences:</strong> Rooftop installation preferred</p>';
        baseData.custom_questions_text = 'Installation preferences: Rooftop installation preferred';
        baseData.has_custom_questions = true;
        break;

      case 'project_comment':
      case 'project_update':
      case 'project_stage_move':
      case 'project_created':
      case 'project_assigned':
      case 'project_assignment':
      case 'project_follower': {
        // Fetch real project data from current tenant
        const realProjects = await this.getRealProjectsFromCurrentTenant();
        if (realProjects.length === 0) {
          // If no projects found, skip this notification type
          console.warn('No projects found in current tenant, skipping project notification test');
          return baseData;
        }
        const selectedProject = realProjects[Math.floor(Math.random() * realProjects.length)];
        
        baseData.project_id = selectedProject.project_id;
        baseData.project_name = selectedProject.project_name;
        baseData.project_type = selectedProject.project_type;
        baseData.pipeline_id = selectedProject.pipeline_id;
        baseData.sender_name = 'Mike Chen';
        baseData.sender_id = null;
        baseData.comment_text = 'The electrical inspection has been completed successfully. All systems are ready for the next phase.';
        baseData.old_stage = 'Electrical Inspection';
        baseData.new_stage = 'System Activation';
        baseData.customer_name = 'Robert Smith';
        baseData.created_by = 'Mike Chen';
        break;
      }

      case 'ticket_created':
      case 'ticket_updated':
      case 'ticket_comment':
      case 'ticket_mention':
      case 'ticket_resolved':
        // Try to fetch a real ticket for the current user for more realistic testing
        const ticketData = await this.getRealisticTicketData();
        baseData.ticket_id = ticketData.ticket_id;
        baseData.ticket_title = ticketData.ticket_title;
        baseData.ticket_description = ticketData.ticket_description;
        baseData.priority = ticketData.priority;
        baseData.status = ticketData.status;
        baseData.created_by = ticketData.created_by;
        baseData.sender_name = ticketData.sender_name;
        break;

      case 'task_assigned':
      case 'task_updated':
      case 'task_completed':
      case 'task_comment':
      case 'task_created':
      case 'task_due': {
        // Try to fetch a real task for the current user for more realistic testing
        const taskData = await this.getRealisticTaskData();
        baseData.task_name = taskData.task_name;
        baseData.task_id = taskData.task_id;
        
        // Use real project data from current tenant for task-related notifications
        const taskProjects = await this.getRealProjectsFromCurrentTenant();
        if (taskProjects.length === 0) {
          console.warn('No projects found in current tenant, skipping task notification test');
          return baseData;
        }
        const selectedTaskProject = taskProjects[Math.floor(Math.random() * taskProjects.length)];
        
        baseData.project_id = selectedTaskProject.project_id;
        baseData.project_name = selectedTaskProject.project_name;
        baseData.project_type = selectedTaskProject.project_type;
        baseData.pipeline_id = selectedTaskProject.pipeline_id;
        baseData.due_date = taskData.due_date;
        baseData.assigned_to = taskData.assigned_to;
        baseData.sender_name = taskData.sender_name;
        baseData.sender_id = null;
        baseData.task_description = taskData.task_description;
        baseData.task_priority = taskData.task_priority;
        baseData.task_status = taskData.task_status;
        break;
      }

      case 'mention':
      case 'tag': {
        // Use real project data from current tenant for mentions
        const mentionProjects = await this.getRealProjectsFromCurrentTenant();
        if (mentionProjects.length === 0) {
          console.warn('No projects found in current tenant, skipping mention notification test');
          return baseData;
        }
        const selectedMentionProject = mentionProjects[Math.floor(Math.random() * mentionProjects.length)];
        
        baseData.mentioned_by = 'Alex Thompson';
        baseData.sender_name = 'Alex Thompson';
        baseData.sender_id = null;
        baseData.context = 'Project Discussion';
        baseData.project_id = selectedMentionProject.project_id;
        baseData.project_name = selectedMentionProject.project_name;
        baseData.project_type = selectedMentionProject.project_type;
        baseData.pipeline_id = selectedMentionProject.pipeline_id;
        baseData.comment_text = 'Please review the updated electrical plans when you have a chance.';
        break;
      }

      case 'project_mention': {
        // Use real project data from current tenant for project mentions
        const projectMentionProjects = await this.getRealProjectsFromCurrentTenant();
        if (projectMentionProjects.length === 0) {
          console.warn('No projects found in current tenant, skipping project mention notification test');
          return baseData;
        }
        const selectedProjectMentionProject = projectMentionProjects[Math.floor(Math.random() * projectMentionProjects.length)];
        
        baseData.mentioned_by = 'Alice Johnson';
        baseData.sender_name = 'Alice Johnson';
        baseData.sender_id = null;
        baseData.project_id = selectedProjectMentionProject.project_id;
        baseData.project_name = selectedProjectMentionProject.project_name;
        baseData.project_type = selectedProjectMentionProject.project_type;
        baseData.pipeline_id = selectedProjectMentionProject.pipeline_id;
        baseData.comment_text = 'Can you please review the updated electrical plans when you have a chance?';
        break;
      }

      case 'ticket_mention':
        baseData.mentioned_by = 'Bob Wilson';
        baseData.sender_name = 'Bob Wilson';
        baseData.sender_id = null;
        baseData.ticket_title = 'Inverter Communication Issue';
        baseData.ticket_id = `TICK-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
        baseData.comment_text = 'Please check the network configuration for this inverter issue.';
        break;

      case 'organization_created':
        baseData.organization_name = 'Green Energy Solutions Inc.';
        baseData.organization_type = 'Commercial';
        baseData.organization_id = 'b8f7c6d5-e4a3-4b2c-8d9e-1f2a3b4c5d6e'; // Use valid UUID format
        baseData.created_by = 'Jennifer Martinez';
        break;

      case 'user_welcome':
        baseData.first_name = 'Allan';
        baseData.last_name = 'Glasier';
        baseData.company_name = 'Joules';
        baseData.user_email = 'allan@example.com';
        // Theme color variables (will be replaced by backend with actual tenant theme colors)
        baseData.primary_color = '#F59E0B';
        baseData.primary_color_light = '#FBBF24';
        baseData.accent_color = '#6366F1';
        baseData.company_logo = 'https://via.placeholder.com/120x40/F59E0B/ffffff?text=SOLERRO';
        break;

      default:
        // Generic fallback
        baseData.project_name = 'Sample Solar Project';
        baseData.customer_name = 'John Doe';
        baseData.created_by = 'Jane Smith';
        baseData.project_type = 'Residential';
        baseData.task_name = 'Site Survey';
        baseData.due_date = this.generateRealisticDate();
        baseData.booking_date = this.generateRealisticDate();
        baseData.booking_time = this.generateRealisticTime();
        baseData.ticket_title = 'Installation Issue';
        baseData.ticket_id = `TICK-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
        baseData.priority = 'High';
        baseData.comment_text = 'This is a realistic comment for testing purposes.';
        baseData.mentioned_by = 'Alice Johnson';
        baseData.context = 'Project Discussion';
        // Theme color variables (will be replaced by backend with actual tenant theme colors)
        baseData.primary_color = '#F59E0B';
        baseData.primary_color_light = '#FBBF24';
        baseData.accent_color = '#6366F1';
        baseData.company_logo = 'https://via.placeholder.com/120x40/F59E0B/ffffff?text=COMPANY';
    }

    return baseData;
  },

  // Generate realistic title and message for a notification type
  async generateRealisticNotification(notificationType: string, existingData?: Record<string, any>): Promise<{ title: string; message: string }> {
    const data = existingData || await this.generateSampleDataForType(notificationType);
    
    switch (notificationType) {
      case 'booking_reminder_1hr_host':
        return {
          title: `[TEST] Appointment Reminder: ${data.project_name || 'Site Survey'}`,
          message: `Your appointment with ${data.customer_name} is scheduled to start in 1 hour on ${data.booking_date} at ${data.booking_time}. Please be ready for your call.`
        };

      case 'booking_reminder_5min_host':
        return {
          title: `[TEST] Appointment Starting Soon: ${data.project_name || 'Site Survey'}`,
          message: `Your appointment with ${data.customer_name} starts in 5 minutes. Please join the meeting now.`
        };

      case 'project_comment':
        return {
          title: `[TEST] New Comment on ${data.project_name}`,
          message: `${data.sender_name} commented: "${data.comment_text}"`
        };

      case 'project_stage_move':
        return {
          title: `[TEST] Project Stage Updated: ${data.project_name}`,
          message: `Project has moved from "${data.old_stage}" to "${data.new_stage}".`
        };

      case 'project_created':
        return {
          title: `[TEST] New Project Created: ${data.project_name}`,
          message: `A new ${data.project_type} project "${data.project_name}" has been created by ${data.created_by}.`
        };

      case 'project_assigned':
        return {
          title: `[TEST] Project Assigned: ${data.project_name}`,
          message: `You have been assigned to the ${data.project_type} project "${data.project_name}".`
        };

      case 'project_update':
        return {
          title: `[TEST] Project Updated: ${data.project_name}`,
          message: `The project "${data.project_name}" has been updated by ${data.sender_name}.`
        };

      case 'ticket_created':
        return {
          title: `[TEST] New Support Ticket: ${data.ticket_title}`,
          message: data.ticket_id 
            ? `A new support ticket has been created with priority ${data.priority}. Ticket ID: ${data.ticket_id}`
            : `A new support ticket "${data.ticket_title}" has been created with priority ${data.priority}.`
        };

      case 'ticket_updated':
        return {
          title: `[TEST] Ticket Updated: ${data.ticket_title}`,
          message: data.ticket_id
            ? `Support ticket ${data.ticket_id} has been updated. Current status: ${data.status}`
            : `Support ticket "${data.ticket_title}" has been updated. Current status: ${data.status}`
        };

      case 'ticket_comment':
        return {
          title: `[TEST] New Comment: ${data.ticket_title}`,
          message: `${data.sender_name} added a comment to ${data.ticket_id ? `ticket ${data.ticket_id}` : `"${data.ticket_title}"`}.`
        };

      case 'ticket_resolved':
        return {
          title: `[TEST] Ticket Resolved: ${data.ticket_title}`,
          message: data.ticket_id
            ? `Support ticket ${data.ticket_id} has been resolved by ${data.sender_name}.`
            : `Support ticket "${data.ticket_title}" has been resolved by ${data.sender_name}.`
        };

      case 'task_assigned':
        return {
          title: `[TEST] Task Assigned: ${data.task_name}`,
          message: `You have been assigned the task "${data.task_name}" by ${data.sender_name}. Due date: ${data.due_date}`
        };

      case 'task_updated':
        return {
          title: `[TEST] Task Updated: ${data.task_name}`,
          message: `The task "${data.task_name}" has been updated by ${data.sender_name}. Current status: ${data.task_status}`
        };

      case 'task_completed':
        return {
          title: `[TEST] Task Completed: ${data.task_name}`,
          message: `The task "${data.task_name}" has been marked as completed by ${data.sender_name}.`
        };

      case 'task_comment':
        return {
          title: `[TEST] New Task Comment: ${data.task_name}`,
          message: `${data.sender_name} added a comment to the task "${data.task_name}".`
        };

      case 'task_created':
        return {
          title: `[TEST] New Task Created: ${data.task_name}`,
          message: `A new task "${data.task_name}" has been created with priority ${data.task_priority}. Due date: ${data.due_date}`
        };

      case 'task_due':
        return {
          title: `[TEST] Task Due Soon: ${data.task_name}`,
          message: `The task "${data.task_name}" is due on ${data.due_date}. Please complete it as soon as possible.`
        };

      case 'mention':
      case 'tag':
        return {
          title: `[TEST] You were mentioned in ${data.project_name}`,
          message: `${data.mentioned_by} mentioned you in a comment: "${data.comment_text}"`
        };

      case 'project_follower':
        return {
          title: `[TEST] New Project Follower: ${data.project_name}`,
          message: `${data.follower_name || data.sender_name} is now following ${data.project_name}.`
        };

      case 'organization_created':
        return {
          title: `[TEST] New Organization Created: ${data.organization_name}`,
          message: `${data.created_by} created a new ${data.organization_type} organization.`
        };

      case 'project_mention':
        return {
          title: `[TEST] You were mentioned in ${data.project_name}`,
          message: `${data.mentioned_by} mentioned you in a project comment: "${data.comment_text}"`
        };

      case 'ticket_mention':
        return {
          title: `[TEST] You were mentioned in ticket: ${data.ticket_title}`,
          message: `${data.mentioned_by} mentioned you in a ticket comment: "${data.comment_text}"`
        };

      case 'user_welcome':
        return {
          title: `[TEST] Welcome to ${data.company_name}!`,
          message: `Hello ${data.first_name} ${data.last_name}, welcome to ${data.company_name}! Your account has been successfully created and you're ready to get started.`
        };

      case 'booking_reminder_1hr_attendee':
      case 'booking_reminder_5min_attendee':
      case 'booking_scheduled_host':
      case 'booking_scheduled_attendee':
        return {
          title: `[TEST] Booking Notification: ${data.project_name || 'Site Survey'}`,
          message: `Booking update for ${data.customer_name} on ${data.booking_date} at ${data.booking_time}.`
        };

      default:
        return {
          title: '[TEST] Test Notification',
          message: 'This is a test notification with realistic data.'
        };
    }
  },

  // Get realistic task data for the current user
  async getRealisticTaskData(): Promise<{
    task_id?: string;
    task_name: string;
    due_date: string;
    assigned_to: string;
    sender_name: string;
    task_description: string;
    task_priority: string;
    task_status: string;
  }> {
    try {
      // Import supabase dynamically to avoid circular dependencies
      const { supabase } = await import('@/lib/supabase');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Try to fetch any recent task for the user (including completed ones for testing)
        const { data: tasks, error } = await supabase
          .from('tasks')
          .select('*')
          .or(`user_id.eq.${user.id},assignee_id.eq.${user.id}`)
          .order('updated_at', { ascending: false })
          .limit(1);

        if (!error && tasks && tasks.length > 0) {
          const task = tasks[0];
          
          // Try to get project details if task has a project_id - but make it completely optional
          let project_name: string | undefined;
          let project_type: string | undefined;
          let project_id: string | undefined;
          
          if (task.project_id) {
            try {
              const { data: project, error } = await supabase
                .from('projects')
                .select('project_name, project_type, id')
                .eq('id', task.project_id)
                .single();
              
              if (!error && project) {
                project_name = project.project_name || undefined;
                project_type = project.project_type || undefined;
                project_id = project.id;
                console.log('[testDataGenerator] Successfully fetched project details:', { project_name, project_type, project_id });
              } else {
                console.log('[testDataGenerator] Could not fetch project details:', error?.message);
                // Don't use fallback project data - keep it undefined
                project_id = undefined;
                project_name = undefined;
                project_type = undefined;
              }
            } catch (projectError) {
              console.log('[testDataGenerator] Error fetching project details:', projectError);
              // Don't use fallback project data - keep it undefined
              project_id = undefined;
              project_name = undefined;
              project_type = undefined;
            }
          }

          // Get assignee name
          let assigned_to = 'You';
          if (task.assignee_id && task.assignee_id !== user.id) {
            try {
              const { data: assignee, error } = await supabase
                .from('users')
                .select('first_name, last_name, email')
                .eq('id', task.assignee_id)
                .single();
              
              if (!error && assignee) {
                assigned_to = assignee.first_name && assignee.last_name
                  ? `${assignee.first_name} ${assignee.last_name}`
                  : assignee.first_name || assignee.last_name || assignee.email?.split('@')[0] || 'User';
              } else {
                assigned_to = 'Team Member';
              }
            } catch {
              assigned_to = 'Team Member';
            }
          }

          // Get sender name (task creator)
          let sender_name = 'System';
          if (task.user_id) {
            try {
              const { data: creator, error } = await supabase
                .from('users')
                .select('first_name, last_name, email')
                .eq('id', task.user_id)
                .single();
              
              if (!error && creator) {
                sender_name = creator.first_name && creator.last_name
                  ? `${creator.first_name} ${creator.last_name}`
                  : creator.first_name || creator.last_name || creator.email?.split('@')[0] || 'User';
              } else {
                sender_name = 'Team Member';
              }
            } catch {
              sender_name = 'Team Member';
            }
          }

          console.log('[testDataGenerator] Found real task:', {
            task_id: task.id,
            task_name: task.title,
            project_name,
            project_type,
            project_id,
            has_project: !!project_id
          });

          return {
            task_id: task.id,
            task_name: task.title,
            due_date: task.due_date || this.generateRealisticDate(),
            assigned_to,
            sender_name,
            task_description: task.description || 'Complete this important task for the project.',
            task_priority: task.priority || 'medium',
            task_status: task.status || 'todo',
          };
        }
      }
    } catch (error) {
      console.log('[testDataGenerator] Could not fetch real task data, using fallback:', error);
    }

    // Fallback to default test data if no real tasks found
    return {
      task_name: 'Site Survey Completion',
      due_date: this.generateRealisticDate(),
      assigned_to: 'Lisa Rodriguez',
      sender_name: 'Tom Anderson',
      task_description: 'Complete the final site survey and prepare installation documentation.',
      task_priority: 'high',
      task_status: 'todo',
    };
  },

  // Get realistic ticket data for the current user
  async getRealisticTicketData(): Promise<{
    ticket_id?: string;
    ticket_title: string;
    ticket_description: string;
    priority: string;
    status: string;
    created_by: string;
    sender_name: string;
  }> {
    try {
      // Import supabase dynamically to avoid circular dependencies
      const { supabase } = await import('@/lib/supabase');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Try to fetch any recent ticket for the user (created by them or assigned to them)
        const { data: tickets, error } = await supabase
          .from('tickets')
          .select('*')
          .or(`created_by.eq.${user.id},assigned_to.eq.${user.id}`)
          .order('updated_at', { ascending: false })
          .limit(1);

        if (!error && tickets && tickets.length > 0) {
          const ticket = tickets[0];

          // Get creator name
          let created_by = 'System';
          let sender_name = 'System';
          if (ticket.created_by) {
            try {
              const { data: creator, error } = await supabase
                .from('users')
                .select('first_name, last_name, email')
                .eq('id', ticket.created_by)
                .single();
              
              if (!error && creator) {
                const creatorName = creator.first_name && creator.last_name
                  ? `${creator.first_name} ${creator.last_name}`
                  : creator.first_name || creator.last_name || creator.email?.split('@')[0] || 'User';
                created_by = creatorName;
                sender_name = creatorName;
              } else {
                created_by = 'Team Member';
                sender_name = 'Team Member';
              }
            } catch {
              created_by = 'Team Member';
              sender_name = 'Team Member';
            }
          }

          console.log('[testDataGenerator] Found real ticket:', {
            ticket_id: ticket.id,
            ticket_title: ticket.title,
            priority: ticket.priority,
            status: ticket.status
          });

          return {
            ticket_id: ticket.id,
            ticket_title: ticket.title || 'Support Request',
            ticket_description: ticket.description || 'This is a support ticket that needs attention.',
            priority: ticket.priority || 'Medium',
            status: ticket.status || 'Open',
            created_by,
            sender_name,
          };
        }
      }
    } catch (error) {
      console.log('[testDataGenerator] Could not fetch real ticket data, using fallback:', error);
    }

    // Fallback to default test data if no real tickets found
    return {
      ticket_title: 'Inverter Communication Issue',
      ticket_description: 'The inverter is not communicating with the monitoring system. Need to check the network connection and firmware version.',
      priority: 'High',
      status: 'Open',
      created_by: 'David Wilson',
      sender_name: 'David Wilson',
    };
  }
}; 